import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import RecommendationTip from '../components/RecommendationTip';
import '../styles/PreferentialProcurementScorecard.scss';
import { themeVars } from '../config/theme';

const fmt = (n) => (Math.round(n * 100) / 100).toFixed(2);
const fmtCurrency = (n) => `R ${Number(n).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const rowClass = (actual, target) => {
  if (actual === null || actual === 0) return 'sc-row--empty';
  return actual >= target ? 'sc-row--achieved' : 'sc-row--partial';
};

const PreferentialProcurementScorecard = () => {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [totalPoints, setTotalPoints] = useState(0);
  const [tmps, setTMPS] = useState(null);
  const [ppSpendSummary, setppSpendSummary] = useState (null);
  const [filename, setFilename] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [noDataSheet, setNoDataSheet] = useState(false);
  const [noTMPS, setNoTMPS] = useState(false);
  const [CombinedPoints, setCombinedPoints] = useState(null);
  const [maxCombinedPoints, setMaxCombinedPoints] = useState(null);
  const [indicators, setIndicators] = useState([]);


  const applyData = (data) => {
    setResults(data.results);
    setTotalPoints(data.total_points);
    setIndicators(data.indicators || []);  //To pull indicators from backend instead of it being hardcoded in the frontend    
    setTMPS(data.tmps ?? null);
    setppSpendSummary(data.pp_spend_summary || null);
    setFilename(data.filename);
    setNoDataSheet(false);
    setError(null);
    setCombinedPoints(null);
  };

  const fetchCombinedTotal = () => {
    api.get('/core/scorecard/combined-total/')
      .then((res) => {
        setCombinedPoints(res.data.combined_points);
        if (res.data.max_points != null) setMaxCombinedPoints(res.data.max_points);
      })
      .catch(() => {});
  };

  const calculateFromDataSheet = async () => {
    setCalculating(true);
    setError(null);
    setNoDataSheet(false);
    try {
      const res = await api.post('/core/scorecard/calculate_from_data_sheet/', { element: 'procurement'});
      applyData(res.data);
      fetchCombinedTotal();
    } catch (err) {
      if (err.response?.status === 404) setNoDataSheet(true);
      else if (err.response?.status === 422) setNoTMPS(true);
      else setError(err.response?.data?.error || 'Failed to calculate from data sheet. Please try again.');
    } finally {
      setCalculating(false);
    }
  };

  useEffect(() => {
    api.get('/core/scorecard/get_scorecard/?element=procurement')
      .then((res) => { applyData(res.data); setLoading(false); })
      .catch((err) => {
        setLoading(false);
        if (err.response?.data?.indicators?.length) setIndicators(err.response.data.indicators);
        if (err.response?.status === 404) calculateFromDataSheet();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchCombinedTotal(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resultMap = results
    ? Object.fromEntries(results.map((r) => [r.id, r]))
    : {};

  const maxPoints = indicators.reduce((s, ind) => s + (ind.points || 0), 0);

  let lastSection = null;

  return (
    <div className="sc-container themed" style={themeVars('procurement')}>
      <div className="sc-header">
        <button className="sc-back" onClick={() => navigate('/esd/preferential-procurement')}>← Back</button>
        <div>
          <h1>Preferential Procurement Scorecard</h1>
          <p>B-BBEE — Preferential Procurement Element Calculation</p>
        </div>
      </div>

      <div className="sc-upload-bar">
        <div className="sc-upload-info">
          {loading
            ? <span className="sc-upload-hint">Loading…</span>
            : filename
              ? <span className="sc-upload-name">✓ Calculated from: <strong>{filename}</strong></span>
              : <span className="sc-upload-hint">No data sheet found</span>
          }
        </div>
        <div className="sc-upload-actions">
          <button
            className="sc-template-btn"
            onClick={() => navigate('/esd/preferential-procurement/submit-datasheet')}
          >
            Go to Documentation
          </button>
          <button
            className="sc-upload-btn"
            onClick={calculateFromDataSheet}
            disabled={calculating || loading}
          >
            {calculating ? 'Calculating…' : 'Recalculate'}
          </button>
        </div>
      </div>

      {noDataSheet && (
        <div className="sc-error">
          No Procurement Data Sheet uploaded yet. Go to{' '}
          <button className="sc-error-link" onClick={() => navigate('/esd/preferential-procurement/submit-datasheet')}>
            Documentation
          </button>{' '}
          and upload the <strong>Procurement Data Sheet</strong> Excel file.
        </div>
      )}

      {noTMPS && (
        <div className="sc-error">
          No TMPS found. Please update your TMPS in{' '}
          <button className="sc-error-link" onClick={() => navigate('/finance-statutory/manage-tmps')}>
            Company Information
          </button>{' '}
          or contact your administrator.
        </div>
      )}

      {error && <div className="sc-error">{error}</div>}

      {ppSpendSummary && ppSpendSummary.length > 0 && (
        <div className="sc-table-outer">
          <h2 className="sc-spend-title">SPEND BREAKDOWN BY CATEGORY</h2>
          <div className="sc-table-wrapper">
            <table className="sc-table sc-spend-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Actual Spend</th>
                  <th>Target Spend</th>
                  <th>Difference</th>
                </tr>
              </thead>
              <tbody>
                {tmps != null && (
                  <tr className="sc-tmps-row">
                    <td><strong>Total Measured Procurement Spend (TMPS)</strong></td>
                    <td colSpan="3">{fmtCurrency(tmps)}</td>
                  </tr>
                )}
                {ppSpendSummary.map((row) => (
                  <tr key={row.category} className={row.spend_diff > 0 ? 'sc-row--achieved' : 'sc-row--partial'}>
                    <td>{row.category}</td>
                    <td>{row.spend != null ? fmtCurrency(row.spend) : '—'}</td>
                    <td>{row.target_spend != null ? fmtCurrency(row.target_spend) : '—'}</td>
                    <td>
                      {row.spend_diff != null ? (
                        <span className={row.spend_diff > 0 ? 'sc-spend-diff sc-spend-diff--pos' : 'sc-spend-diff sc-spend-diff--neg'}>
                          {row.spend_diff > 0 ? '+' : ''}{fmtCurrency(row.spend_diff)}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="sc-table-outer">
        <div className="sc-table-wrapper">
          <table className="sc-table">
            <thead>
              <tr>
                <th>Indicator</th>
                <th>Compliance Target</th>
                <th>Max Points</th>
                <th>Actual (%)</th>
                <th>Points Achieved</th>
              </tr>
            </thead>
            <tbody>
              {indicators.map((ind) => {
                const showHeader = ind.section && ind.section !== lastSection;
                lastSection = ind.section;

                const r = resultMap[ind.id];
                const actual = r?.actual ?? null;
                const pts = r?.points ?? 0;
                const isMax = pts >= ind.points;

                return (
                  <React.Fragment key={ind.id}>
                    {showHeader && (
                      <tr className="sc-row--section-header">
                        <td colSpan="5">{ind.section}</td>
                      </tr>
                    )}

                  <tr key={ind.id} className={rowClass(actual, ind.target)}>
                    <td>{ind.label}</td>
                    <td>{ind.targetLabel ?? `${ind.target}%`}</td>
                    <td>{ind.points}</td>
                    <td>{actual !== null ? `${fmt(actual)}%` : '—'}</td>
                    <td>
                      <span className={`sc-points${isMax ? ' sc-points--max' : pts === 0 ? ' sc-points--zero' : ''}`}>
                        {fmt(pts)}
                      </span>
                      <RecommendationTip
                        ind={ind}
                        actual={actual}
                        pts={pts}
                        spendRow={ppSpendSummary?.find(r => r.category === ind.id)}
                      />
                    </td>
                  </tr>
                </React.Fragment>
                );
              })}
              <tr className={`sc-total-row${results?.some(r => r.id === '_total_sub_min' && r.sub_minimum_met === false) ? ' sc-row--submin-fail' : ''}`}>
                <td colSpan="3">Total</td>
                <td />
                <td>{fmt(totalPoints)} / {maxPoints || '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {results?.some(r => r.sub_minimum_met === false) && (
          <div className="sc-submin-icon-wrap">
            <span className="sc-submin-icon-btn">⚠</span>
            <div className="sc-submin-popup">
              Sub-minimum not met — overall scorecard level will be discounted by one level.
            </div>
          </div>
        )}
      </div>

      <div className="sc-summary">
        <div className="sc-summary-card sc-summary-card--total">
          <div className="sc-summary-score">{fmt(totalPoints)} <span>/ {maxPoints || '—'}</span></div>
          <div className="sc-summary-label">Total Preferential Procurement Points</div>
        </div>
        <div className="sc-summary-card">
          <div className="sc-summary-score">{CombinedPoints !== null ? fmt(CombinedPoints) : '-'} <span>/ {maxCombinedPoints ?? '—'}</span></div>
          <div className="sc-summary-label">Points (incl. bonus)</div>
        </div>
        <div className="sc-summary-card">
          <div className="sc-summary-score">{maxPoints > 0 ? Math.round((totalPoints / maxPoints) * 100) : '—'}<span>%</span></div>
          <div className="sc-summary-label">Preferential Procurement Achievement</div>
        </div>
      </div>

    </div>
  );
};

export default PreferentialProcurementScorecard;
